import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { UpdateAccountProfileAPI, UpdatePasswordAPI } from "../../../api/Auth/AccountController";
import { toast } from "react-toastify";
import LoadingButton from "@mui/lab/LoadingButton";

const UserEditDialogComponent = ({ open, onClose, setopenUserEditCreateDialog, rowData, onRefresh }) => {
  const [editMode, setEditMode] = useState("profile"); // Default mode
  const [userName, setUserName] = useState(rowData.userName);
  const [email, setEmail] = useState(rowData.email);
  const [userRole, setUserRole] = useState(rowData.userRole);
  const [phoneNumber, setPhoneNumber] = useState(rowData.phoneNumber || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleUserEditDialogClose = () => {
    setopenUserEditCreateDialog(false);
    onClose();
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    const UserId = rowData.user_Id;

    if (editMode === "profile") {
      const formData = new FormData();
      formData.append("UserName", userName);
      formData.append("Email", email);
      formData.append("UserRole", userRole);
      formData.append("User_Id", UserId);
      formData.append("PhoneNumber", phoneNumber);
      await UpdateAccountProfileAPI(formData, setIsLoading, toast, onRefresh);
      onClose();
    }

    if (editMode === "password") {
      if (password !== confirmPassword) {
        toast.error("Passwords do not match!");
        setIsLoading(false);
        return;
      }

      const payload = {
        UserId: UserId,
        OldPassword: oldPassword,
        NewPassword: password,
      };

      await UpdatePasswordAPI(payload, setIsLoading, toast);
      onRefresh();
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={null} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Edit User Account</Typography>
          <IconButton
            aria-label="close"
            onClick={handleUserEditDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Radio Buttons for Selection */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <RadioGroup
              row
              value={editMode}
              onChange={(e) => setEditMode(e.target.value)}
            >
              <FormControlLabel
                value="profile"
                control={<Radio />}
                label="Profile Update"
              />
              <FormControlLabel
                value="password"
                control={<Radio />}
                label="Password Update"
              />
            </RadioGroup>
          </Box>

          {/* Profile Update Fields */}
          {editMode === "profile" && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="User Name"
                fullWidth
                variant="outlined"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Phone Number"
                type="text"
                fullWidth
                variant="outlined"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <TextField
                margin="dense"
                label="User Role"
                select
                fullWidth
                variant="outlined"
                value={userRole}
                onChange={(e) => setUserRole(e.target.value)}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
              </TextField>
            </>
          )}

          {/* Password Update Fields */}
          {editMode === "password" && (
            <>
              <TextField
                margin="dense"
                label="Old Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="dense"
                label="New Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="dense"
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {/* Action Buttons */}
          <DialogActions style={{ padding: "16px 24px" }}>
            {isLoading ? (
              <LoadingButton loading variant="outlined" color="primary">
                Submitting...
              </LoadingButton>
            ) : (
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="primary"
                style={{
                  padding: "8px 24px",
                  width: 123,
                  height: 44,
                  fontSize: "16px",
                  textTransform: "none",
                }}
              >
                Update
              </Button>
            )}
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserEditDialogComponent;
